<template>
  <div class="w-full bg-white border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] rounded-2xl md:rounded-3xl overflow-hidden">
    <Tabs :value="activeIndex" scrollable class="w-full detail-tab">
      <TabList>
        <Tab v-for="(item, index) in enabledTabs" :key="index" :value="index">
          <div class="flex items-center justify-center gap-2">
            <i v-if="item.icon" :class="`pi ${item.icon}`"></i>
            <span v-if="item.title" class="text-[13px] lg:text-[14px] font-bold uppercase tracking-wider">{{ item.title }}</span>
          </div>
        </Tab>
      </TabList>
      <TabPanels>
        <TabPanel
          v-for="(item, index) in enabledTabs"
          :key="index"
          :value="index"
        >
          <div class="w-full gap-3 lg:gap-4 grid">
            <div v-if="item.youtubeLink" class="w-full lg:w-9/12 mx-auto">
              <iframe
                :src="getEmbedSrc(item.youtubeLink)"
                :title="item?.title ?? 'YouTube video player'"
                frameborder="0"
                allow="
                  accelerometer;
                  autoplay;
                  clipboard-write;
                  encrypted-media;
                  gyroscope;
                  picture-in-picture;
                  web-share;
                "
                referrerpolicy="strict-origin-when-cross-origin"
                allowfullscreen
                class="w-full aspect-video m-0 block rounded-xl"
              ></iframe>
            </div>
            <div
              v-if="item.description"
              v-html="item.description"
              class="text-[14px] md:text-[15px] font-medium text-gray-600 leading-relaxed"
            ></div>

            <div v-if="getEnabledFeatures(item).length" class="w-full rounded-xl overflow-hidden">
              <div
                v-for="(feature, fIndex) in getEnabledFeatures(item)"
                :key="`div-${feature.id ?? fIndex}`"
                class="flex flex-col lg:flex-row py-2.5 lg:py-3 px-3 lg:px-5"
                :class="fIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'"
              >
                <div v-if="feature?.title" class="font-bold text-[13px] text-gray-800 w-full lg:w-[160px] shrink-0">
                  {{ feature.title }}:
                </div>
                <div
                  v-if="feature?.description"
                  v-html="feature.description"
                  class="w-full text-[13px] text-gray-500 font-medium leading-relaxed"
                ></div>
              </div>
            </div>
            <AtomsButton
              v-if="item?.button?.enabled"
              :label="item?.button?.label"
              :link="item?.button?.link"
              :target="item?.button?.target"
              :icon="item?.button?.icon"
              :iconPosition="item?.button?.iconPosition"
              :color="item?.button?.color"
              :disabled="item?.button?.disabled"
            />
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </div>
</template>

<script setup>
const props = defineProps({
  tabs: {
    type: Array,
  },
});

import { useSiteSettingsStore } from "@/store/index";
const siteSettingsStore = useSiteSettingsStore();
const activeIndex = ref(0);
const enabledTabs = computed(() => props.tabs.filter((tab) => tab.enabled));
const normalizeArray = (value) => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (Array.isArray(value?.data)) {
    return value.data.map((item) =>
      item?.attributes ? { id: item.id, ...item.attributes } : item,
    );
  }
  return [];
};

const getEnabledRows = (rows) =>
  normalizeArray(rows).filter((feature) => feature?.enabled ?? true);

const getEnabledFeatures = (item) => {
  // Prefer direct features/productFeatures on the tab item.
  const directFeatures = getEnabledRows(item?.productFeatures || item?.features);
  if (directFeatures.length) return directFeatures;

  // Fall back to productTechnical groups. Use only the last group to avoid
  // duplication from deep populate (pLevel) pulling in extra entries.
  const groups = normalizeArray(item?.productTechnical);
  if (groups.length) {
    const lastGroup = groups[groups.length - 1];
    return getEnabledRows(lastGroup?.items);
  }
  return [];
};

const getEmbedSrc = (link) => {
  const videoId = link?.split('v=')[1];
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1`;
};
</script>

<style lang="scss">
.detail-tab {
  .p-tabpanel {
    p {
      @apply mb-5 last:mb-0;
    }
  }
}

/* PrimeVue Tab overrides for clean boxed look */
.detail-tab .p-tablist {
  border-bottom: 1px solid #f3f4f6 !important;
}
.detail-tab .p-tab {
  padding: 1rem 1.5rem !important;
  color: #9ca3af !important;
  font-weight: 600 !important;
  transition: all 0.2s ease;
  border: none !important;
  background: transparent !important;
}
.detail-tab .p-tab:hover {
  color: #374151 !important;
}
.detail-tab .p-tab-active {
  color: #111827 !important;
  font-weight: 700 !important;
  border-bottom: 2px solid #d1d5db !important;
}
.detail-tab .p-tablist-active-bar {
  background-color: #d1d5db !important;
}
</style>
